/**
 * ICSC BOSS — App Layout
 * Sidebar + Topbar shell that wraps all authenticated pages.
 * Mobile-first: hamburger + slide-in sidebar on <768 px, fixed sidebar on ≥768 px.
 */
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BRAND } from "../config/branding";
import { colors, spacing, typography, radius, shadows, layout, zIndex } from "../design-system/tokens.js";

// ── Nav structure ──────────────────────────────────────────────────────────────

const NAV_GROUPS = [
  {
    id: "dashboard",
    label: null,
    items: [
      { label: "Dashboard",          to: "/",                  icon: "🏠", roles: ["super_admin", "third_party_admin", "certification_body", "client", "learner"] },
      { label: "ISO Docs",           to: "/documents",          icon: "📄", roles: ["super_admin", "third_party_admin"] },
      { label: "My Audit Feedback",  to: "/my-audit-feedback",  icon: "📝", roles: ["client"] },
    ],
  },
  {
    id: "accreditation",
    label: "Accreditation",
    roles: ["super_admin"],
    items: [
      { label: "Overview",              to: "/accreditation",                        icon: "🏛️" },
      { label: "Cert Bodies",           to: "/accreditation/cert-bodies",            icon: "🏢" },
      { label: "Applications",          to: "/admin/applications",                   icon: "📋" },
      { label: "Auditors",              to: "/accreditation/auditors",               icon: "👤" },
      { label: "Trainers & Assessors",  to: "/accreditation/trainer-assessors",      icon: "🧑‍🏫" },
      { label: "Courses",               to: "/accreditation/courses",                icon: "📚" },
      { label: "Course Accreditations", to: "/accreditation/course-accreditations",  icon: "✅" },
      { label: "Monitoring",            to: "/accreditation/monitoring",             icon: "♻️" },
      { label: "Scopes",                to: "/accreditation/scopes",                 icon: "📋" },
      { label: "Rules",                 to: "/accreditation/rules",                  icon: "📏" },
    ],
  },
  {
    id: "governance",
    label: "Governance",
    roles: ["super_admin"],
    items: [
      { label: "Surveillance",        to: "/governance/surveillance",        icon: "🔍" },
      { label: "Renewals",            to: "/governance/renewals",            icon: "🔄" },
      { label: "Acc. Certificates",   to: "/governance/acc-certificates",    icon: "🎖️" },
      { label: "Auditor Performance", to: "/governance/auditor-performance", icon: "📊" },
      { label: "Audit Calendar",      to: "/calendar",                       icon: "📅" },
      { label: "CAPA",                to: "/capa",                           icon: "⚡" },
      { label: "Remote Audit",        to: "/remote-audit",                   icon: "🖥️" },
    ],
  },
  {
    id: "membership",
    label: "Membership",
    roles: ["super_admin"],
    items: [
      { label: "Membership Dashboard", to: "/membership",          icon: "👥" },
      { label: "Verify Membership",    href: "https://councilforstandards.org/verify/membership",   icon: "✓", external: true  },
    ],
  },
  {
    id: "certificates",
    label: "Certificates",
    roles: ["super_admin", "third_party_admin"],
    items: [
      { label: "Certificate Manager", to: "/certificates",          icon: "📜" },
      { label: "Lifecycle Dashboard", to: "/certificate-lifecycle", icon: "📈" },
      { label: "ISO Certificates",    to: "/iso-certificates",      icon: "🏆" },
      { label: "CPD Certificates",    to: "/cpd-certificates",      icon: "🎓" },
      { label: "Assessments",         to: "/company-assessments",   icon: "📝" },
    ],
  },
  {
    id: "clients",
    label: "Operations",
    roles: ["super_admin", "third_party_admin"],
    items: [
      { label: "Cert Bodies",    to: "/cert-bodies",    icon: "🏢", roles: ["super_admin"] },
      { label: "Clients",        to: "/clients",        icon: "👥", roles: ["super_admin", "third_party_admin"] },
      { label: "Evidence Review",  to: "/evidence-review",  icon: "🔍", roles: ["super_admin", "third_party_admin"] },
      { label: "AI Evidence Review", to: "/ai-review",     icon: "✦",  roles: ["super_admin", "third_party_admin"] },
      { label: "Audit Feedback",   to: "/audit-feedback",   icon: "📝", roles: ["super_admin", "third_party_admin"] },
      { label: "Staff Competency", to: "/staff-competency", icon: "🎓", roles: ["super_admin", "third_party_admin", "certification_body"] },
      { label: "Invoices",       to: "/invoices",       icon: "💳" },
      { label: "Tickets",        to: "/tickets",        icon: "🎫" },
      { label: "Evidence",       to: "/evidence",       icon: "📁", roles: ["super_admin"] },
    ],
  },
  {
    id: "learning",
    label: "Learning",
    roles: ["super_admin", "third_party_admin", "certification_body", "client", "learner"],
    items: [
      { label: "Course Manager",  to: "/courses",            icon: "🎬", roles: ["super_admin"] },
      { label: "SCORM Import",    to: "/scorm-import",       icon: "📦", roles: ["super_admin"] },
      { label: "My Courses",      to: "/my-courses",         icon: "📖" },
      { label: "Allowed Courses", to: "/allowed-courses",    icon: "✅", roles: ["third_party_admin", "certification_body"] },
      { label: "Assign Courses",  to: "/assign-courses",     icon: "📋", roles: ["super_admin", "third_party_admin", "certification_body", "client"] },
      { label: "Competency",       to: "/competency/courses",    icon: "🎯", roles: ["super_admin", "third_party_admin", "certification_body"] },
      { label: "My Competency",    to: "/competency/my-courses", icon: "🏅", roles: ["client", "learner"] },
    ],
  },
  {
    id: "cb_portal",
    label: "My CB Portal",
    roles: ["third_party_admin"],
    items: [
      { label: "My Accreditation",   to: "/my-accreditation",        icon: "🏛️" },
      { label: "CB Accred. Status",  to: "/cb-accreditation-status", icon: "📊" },
      { label: "Reviewer Dashboard", to: "/reviewer-dashboard",       icon: "🔎" },
      { label: "Apply for Accreditation", to: "/apply",              icon: "📝" },
    ],
  },
  {
    id: "shop",
    label: "Shop",
    roles: ["super_admin", "third_party_admin", "certification_body", "client", "learner"],
    items: [
      { label: "The ICSC Store",       to: "/store",       icon: "🏪", roles: ["super_admin", "third_party_admin", "certification_body", "client", "learner"] },
      { label: "Shop Management",     to: "/shop/manage", icon: "⚙️", roles: ["super_admin"] },
    ],
  },
  {
    id: "system",
    label: "System",
    roles: ["super_admin"],
    items: [
      { label: "Analytics",       to: "/analytics",          icon: "📊" },
      { label: "Security Audit",  to: "/audit-logs",         icon: "🔒" },
      { label: "User Management", to: "/users",              icon: "👥" },
      { label: "SLA Management",  to: "/sla-management",     icon: "⏱️" },
      { label: "Compliance",      to: "/compliance",         icon: "✅" },
      { label: "Email Settings",  to: "/settings/email",     icon: "📧" },
      { label: "Webhooks",        to: "/settings/webhooks",  icon: "🔗" },
      { label: "Settings",        to: "/settings",           icon: "⚙️" },
    ],
  },
];

// ── SidebarItem ────────────────────────────────────────────────────────────────

function SidebarItem({ item, isActive, onClose }) {
  const [hovered, setHovered] = useState(false);
  const sharedStyle = {
    display: "flex",
    alignItems: "center",
    padding: `7px ${spacing.md}`,
    borderRadius: radius.md,
    marginBottom: 2,
    textDecoration: "none",
    fontSize: typography.fontSizes.md,
    fontWeight: isActive ? typography.fontWeights.semibold : typography.fontWeights.medium,
    color: isActive ? "#ffffff" : hovered ? "#e2e8f0" : colors.sidebarText,
    background: isActive ? colors.sidebarActive : hovered ? colors.sidebarHover : "transparent",
    transition: "background 120ms ease, color 120ms ease",
    minHeight: "40px",
  };
  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noreferrer noopener"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={sharedStyle}
      >
        <span style={{ lineHeight: 1.3 }}>{item.label}</span>
      </a>
    );
  }
  return (
    <Link
      to={item.to}
      onClick={onClose}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={sharedStyle}
    >
      <span style={{ lineHeight: 1.3 }}>{item.label}</span>
    </Link>
  );
}

// ── SidebarGroup ───────────────────────────────────────────────────────────────

function SidebarGroup({ group, role, location, onClose, collapsible = true }) {
  if (group.roles && !group.roles.includes(role)) return null;

  const visibleItems = group.items.filter(
    (item) => !item.roles || item.roles.includes(role)
  );
  if (visibleItems.length === 0) return null;

  const [open, setOpen] = useState(true);

  return (
    <div style={{ marginBottom: spacing.sm }}>
      {group.label && (
        <button
          onClick={() => collapsible && setOpen((o) => !o)}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            width: "100%",
            padding: `${spacing.xs} ${spacing.md}`,
            background: "none", border: "none",
            color: colors.sidebarHeading,
            fontSize: typography.fontSizes.xs,
            fontWeight: typography.fontWeights.semibold,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: collapsible ? "pointer" : "default",
            marginBottom: spacing.xs,
            minHeight: "36px",
          }}
        >
          <span>{group.label}</span>
          {collapsible && <span style={{ fontSize: "10px", opacity: 0.6 }}>{open ? "▾" : "▸"}</span>}
        </button>
      )}
      {open && (
        <div style={{ paddingLeft: group.label ? spacing.xs : 0 }}>
          {visibleItems.map((item) => (
            <SidebarItem
              key={item.to || item.href}
              item={item}
              isActive={!item.external && (location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to)))}
              onClose={onClose}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────────

function Sidebar({ role, open, onClose }) {
  const location = useLocation();

  return (
    <aside
      className={`app-sidebar${open ? " sidebar-open" : ""}`}
      style={{
        width: layout.sidebarWidth,
        background: colors.sidebarBg,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: zIndex.sidebar,
        borderRight: `1px solid ${colors.sidebarBorder}`,
        overflowY: "auto",
      }}
    >
      {/* Brand + close button */}
      <div style={{
        padding: `${spacing.lg} ${spacing.md}`,
        borderBottom: `1px solid ${colors.sidebarBorder}`,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div>
          <div style={{ color: "#fff", fontWeight: typography.fontWeights.bold, fontSize: typography.fontSizes.xl, lineHeight: 1.2 }}>
            {BRAND.name}
          </div>
          <div style={{ color: colors.sidebarHeading, fontSize: typography.fontSizes.xs, marginTop: "2px", letterSpacing: "0.04em" }}>
            {BRAND.short}
          </div>
        </div>
        {/* Close button — only visible on mobile via CSS */}
        <button
          className="hamburger-btn"
          onClick={onClose}
          aria-label="Close navigation"
          style={{ color: colors.sidebarHeading, fontSize: "18px" }}
        >
          ✕
        </button>
      </div>

      {/* Nav groups */}
      <nav style={{ flex: 1, padding: `${spacing.md} ${spacing.sm}`, overflowY: "auto" }} aria-label="Main navigation">
        {NAV_GROUPS.map((group) => (
          <SidebarGroup
            key={group.id}
            group={group}
            role={role}
            location={location}
            onClose={onClose}
          />
        ))}
      </nav>

      {/* Footer links */}
      <div style={{
        padding: `${spacing.sm} ${spacing.md}`,
        borderTop: `1px solid ${colors.sidebarBorder}`,
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", gap: spacing.md, justifyContent: "center" }}>
          {[{ label: "Privacy", to: "/privacy" }, { label: "Terms", to: "/terms" }].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={onClose}
              style={{ color: colors.sidebarHeading, fontSize: typography.fontSizes.xs, textDecoration: "none" }}
            >
              {l.label}
            </Link>
          ))}
        </div>
        <div style={{ color: colors.sidebarHeading, fontSize: "10px", textAlign: "center", marginTop: spacing.xs, opacity: 0.6 }}>
          v2.0 © {new Date().getFullYear()} {BRAND.short}
        </div>
      </div>
    </aside>
  );
}

// ── Topbar ─────────────────────────────────────────────────────────────────────

function Topbar({ user, role, onLogout, onMenuClick }) {
  const roleLabels = {
    super_admin:         "System Administrator",
    third_party_admin:   "Accredited Body Admin",
    certification_body:  "Certification Body",
    client:              "Client Administrator",
    learner:             "Learner",
  };

  return (
    <header style={{
      height: layout.topbarHeight,
      background: colors.topbarBg,
      borderBottom: `1px solid ${colors.topbarBorder}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: `0 ${spacing.lg}`,
      position: "sticky",
      top: 0,
      zIndex: zIndex.topbar,
      boxShadow: shadows.xs,
    }}>
      {/* Left — hamburger (visible on mobile only via CSS) */}
      <button
        className="hamburger-btn"
        onClick={onMenuClick}
        aria-label="Toggle navigation menu"
        aria-expanded={undefined}
        style={{ color: colors.textSecondary }}
      >
        ☰
      </button>

      {/* Right — role badge + user + sign out */}
      <div style={{ display: "flex", alignItems: "center", gap: spacing.md, marginLeft: "auto" }}>
        {/* Role badge — hidden on mobile */}
        <span
          className="hide-mobile"
          style={{
            background: colors.primaryLight,
            color: colors.primary,
            borderRadius: radius.pill,
            padding: "3px 12px",
            fontSize: typography.fontSizes.xs,
            fontWeight: typography.fontWeights.semibold,
          }}
        >
          {roleLabels[role] || role}
        </span>

        {/* User avatar + name */}
        <Link
          to="/profile"
          style={{ display: "flex", alignItems: "center", gap: spacing.sm, textDecoration: "none", color: colors.textPrimary }}
        >
          <div style={{
            width: 32, height: 32,
            background: colors.primaryLight,
            borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: typography.fontSizes.md,
            fontWeight: typography.fontWeights.bold,
            color: colors.primary,
            flexShrink: 0,
          }}>
            {(user.full_name || user.username || "U")[0].toUpperCase()}
          </div>
          <div className="hide-mobile">
            <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.semibold, lineHeight: 1.2 }}>
              {user.full_name || user.username || "User"}
            </div>
            <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>Settings</div>
          </div>
        </Link>

        {/* Divider */}
        <div style={{ width: 1, height: 28, background: colors.border }} />

        {/* Sign out */}
        <button
          onClick={onLogout}
          style={{
            background: "none",
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            padding: "6px 14px",
            fontSize: typography.fontSizes.sm,
            fontWeight: typography.fontWeights.medium,
            color: colors.textSecondary,
            cursor: "pointer",
            transition: "border-color 150ms, color 150ms",
            minHeight: "36px",
          }}
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

// ── AppLayout (main export) ────────────────────────────────────────────────────

export default function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const role = user.role || localStorage.getItem("userRole") || "client";

  const handleLogout = () => {
    ["token", "user", "userRole", "userCompanyId"].forEach((k) => localStorage.removeItem(k));
    window.location.href = "/login";
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: colors.background }}>
      {/* Mobile overlay backdrop */}
      <div
        className={`sidebar-overlay${sidebarOpen ? " sidebar-open" : ""}`}
        onClick={closeSidebar}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <Sidebar role={role} open={sidebarOpen} onClose={closeSidebar} />

      {/* Main content area */}
      <div
        className="app-main"
        style={{
          flex: 1,
          marginLeft: layout.sidebarWidth,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Topbar
          user={user}
          role={role}
          onLogout={handleLogout}
          onMenuClick={() => setSidebarOpen((o) => !o)}
        />

        <main
          className="app-content"
          style={{
            flex: 1,
            padding: `${spacing.xl} ${spacing.xl}`,
            maxWidth: "1280px",
            width: "100%",
            margin: "0 auto",
            boxSizing: "border-box",
          }}
        >
          {children}
        </main>

        <footer style={{
          padding: `${spacing.md} ${spacing.xl}`,
          borderTop: `1px solid ${colors.border}`,
          fontSize: typography.fontSizes.xs,
          color: colors.textMuted,
          textAlign: "center",
        }}>
          {BRAND.name} is proprietary software owned by {BRAND.short} — International Council for Standards &amp; Certification.
        </footer>
      </div>
    </div>
  );
}
